import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Child } from '../../types';
import { formatDate, formatMoney } from '../../utils/format';

interface UnmatchedTransaction {
    id: number;
    sender: string;
    title: string;
    amount: number;
    transactionDate: string;
    mappedChildren: Child[];
}

// One parent account paying for several children (twins): the transaction
// needs a manual decision how to divide the amount between them.
const SplitAssignment: React.FC<{
    transaction: UnmatchedTransaction;
    onAssign: () => void;
}> = ({ transaction, onAssign }) => {
    const twins = transaction.mappedChildren;
    const evenShare = (transaction.amount / twins.length).toFixed(2);
    const [amounts, setAmounts] = useState<{ [childId: number]: string }>(
        () => Object.fromEntries(twins.map(c => [c.id, evenShare]))
    );
    const [error, setError] = useState<string | null>(null);

    const total = twins.reduce((sum, c) => sum + (parseFloat(amounts[c.id]) || 0), 0);
    const totalMatches = Math.abs(total - transaction.amount) < 0.005;

    const setPreset = (preset: { [childId: number]: string }) => {
        setAmounts(preset);
        setError(null);
    };

    const presetAllTo = (childId: number) =>
        setPreset(Object.fromEntries(twins.map(c => [c.id, c.id === childId ? transaction.amount.toFixed(2) : '0'])));

    const presetEven = () =>
        setPreset(Object.fromEntries(twins.map(c => [c.id, evenShare])));

    const handleSubmit = () => {
        const allocations = twins
            .map(c => ({ childId: c.id, amount: parseFloat(amounts[c.id]) || 0 }))
            .filter(a => a.amount !== 0);
        if (allocations.length === 0 || !totalMatches) {
            setError(`Kwoty muszą sumować się do ${formatMoney(transaction.amount)}`);
            return;
        }
        axios.post('/api/assignment/split', {
            unmatchedTransactionId: transaction.id,
            allocations,
        }).then(() => {
            onAssign();
        }).catch(err => {
            setError(err.response?.data?.error || 'Nie udało się przypisać podziału');
        });
    };

    return (
        <div className="split-assignment">
            <div className="d-flex flex-wrap gap-2 mb-3">
                {twins.map(child => (
                    <button key={child.id} type="button" className="btn btn-sm btn-secondary"
                            onClick={() => presetAllTo(child.id)}>
                        Wszystko: {child.name.split(' ')[0]}
                    </button>
                ))}
                <button type="button" className="btn btn-sm btn-secondary" onClick={presetEven}>
                    Po równo
                </button>
            </div>
            <div className="d-flex flex-wrap gap-3 align-items-center">
                {twins.map(child => (
                    <div key={child.id} className="d-flex align-items-center gap-2">
                        <label className="small text-nowrap" htmlFor={`split-${transaction.id}-${child.id}`}>
                            {child.name.split(' ')[0]}
                        </label>
                        <input
                            id={`split-${transaction.id}-${child.id}`}
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm split-amount-input"
                            value={amounts[child.id]}
                            onChange={(e) => {
                                setAmounts(prev => ({ ...prev, [child.id]: e.target.value }));
                                setError(null);
                            }}
                        />
                        <span className="small text-muted">zł</span>
                    </div>
                ))}
                <button className="btn btn-sm btn-primary" onClick={handleSubmit} disabled={!totalMatches}>
                    Przypisz podział
                </button>
            </div>
            {!totalMatches && (
                <div className="field-hint warn">
                    Suma {formatMoney(total, { fixed: true })} z {formatMoney(transaction.amount, { fixed: true })} — kwoty muszą się zgadzać.
                </div>
            )}
            {error && <div className="field-hint error" role="alert">{error}</div>}
        </div>
    );
};

const UnmatchedTransactionsList: React.FC<{ refresh: boolean, onAssign: () => void }> = ({ refresh, onAssign }) => {
    const [unmatchedTransactions, setUnmatchedTransactions] = useState<UnmatchedTransaction[]>([]);
    const [children, setChildren] = useState<Child[]>([]);
    const [assigneeNames, setAssigneeNames] = useState<{[key: number]: string}>({});

    useEffect(() => {
        axios.get<UnmatchedTransaction[]>('/api/data/unmatched')
            .then(response => {
                setUnmatchedTransactions(response.data);
            });
        axios.get<Child[]>('/api/data/children')
            .then(response => {
                setChildren(response.data);
            });
    }, [refresh]);

    const handleAssign = (unmatchedTransactionId: number) => {
        const assigneeName = (assigneeNames[unmatchedTransactionId] || '').trim();
        if (!assigneeName) {
            return;
        }

        const existingChild = children.find(child => child.name === assigneeName);

        if (existingChild) {
            axios.post('/api/assignment', null, {
                params: {
                    unmatchedTransactionId,
                    childId: existingChild.id
                }
            }).then(() => {
                onAssign();
                setAssigneeNames(prev => ({...prev, [unmatchedTransactionId]: ''}));
            });
        } else {
            axios.post('/api/assignment/new-child', null, {
                params: {
                    unmatchedTransactionId,
                    childName: assigneeName
                }
            }).then(() => {
                onAssign();
                setAssigneeNames(prev => ({...prev, [unmatchedTransactionId]: ''}));
            });
        }
    };

    const handleAssigneeNameChange = (transactionId: number, name: string) => {
        setAssigneeNames(prev => ({...prev, [transactionId]: name}));
    };

    if (unmatchedTransactions.length === 0) {
        return null; // Hide the component if there are no unmatched transactions
    }

    return (
        <section className="panel surface panel-attention">
            <div className="panel-head">
                <h2 className="section-title">Niedopasowane transakcje</h2>
                <span className="count-badge" aria-label={`Liczba: ${unmatchedTransactions.length}`}>{unmatchedTransactions.length}</span>
            </div>
            <p className="section-hint mb-0">
                Te wpłaty nie trafiły jeszcze do żadnego dziecka. Wybierz osobę z listy i zatwierdź —
                wpisanie nowego imienia i nazwiska doda dziecko do klasy.
            </p>

            <datalist id="children-list">
                {children.map((child) => (
                    <option key={child.id} value={child.name} />
                ))}
            </datalist>

            <ul className="txn-list">
                {unmatchedTransactions.map((transaction) => {
                    const isMultiChild = (transaction.mappedChildren?.length || 0) >= 2;
                    const typedName = (assigneeNames[transaction.id] || '').trim();
                    const isNewChild = typedName !== '' && !children.some(child => child.name === typedName);
                    return (
                        <li key={transaction.id} className="txn-item">
                            <div className="txn-info">
                                <div className="txn-amount">
                                    {formatMoney(transaction.amount)}
                                    <span className="txn-meta"> · {formatDate(transaction.transactionDate)}</span>
                                </div>
                                <div className="txn-sender">{transaction.sender}</div>
                                <div className="txn-title">{transaction.title}</div>
                                {isMultiChild && (
                                    <div className="txn-meta">
                                        Konto opłaca: {transaction.mappedChildren.map(c => c.name).join(', ')}
                                    </div>
                                )}
                            </div>
                            <div className="txn-action">
                                {isMultiChild ? (
                                    <SplitAssignment transaction={transaction} onAssign={onAssign} />
                                ) : (
                                    <form
                                        onSubmit={(e) => { e.preventDefault(); handleAssign(transaction.id); }}
                                    >
                                        <div className="assign-form">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Imię i nazwisko dziecka"
                                                aria-label={`Przypisz wpłatę od ${transaction.sender} do dziecka`}
                                                value={assigneeNames[transaction.id] || ''}
                                                onChange={(e) => handleAssigneeNameChange(transaction.id, e.target.value)}
                                                list="children-list"
                                                autoComplete="off"
                                            />
                                            <button type="submit" className="btn btn-primary" disabled={!typedName}>
                                                {isNewChild ? 'Dodaj i przypisz' : 'Przypisz'}
                                            </button>
                                        </div>
                                        {isNewChild && (
                                            <div className="field-hint warn">Nie ma takiego dziecka w klasie — zostanie dodane jako nowe.</div>
                                        )}
                                    </form>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default UnmatchedTransactionsList;
